import { default as React } from 'react';
import { useEffect, useState, useCallback } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  useApi,
  fetchApiRef,
  discoveryApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Autocomplete from '@mui/material/Autocomplete';

export const ADOProjectSelector = ({
  onChange,
  value,
  disabled,
}: FieldExtensionComponentProps<string>) => {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);

  const [projects, setProjects] = useState<Array<{ id: string; name: string }>>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [validating, setValidating] = useState(false);
  const [accessValidated, setAccessValidated] = useState<boolean | null>(null);

  const validateProjectAccess = useCallback(
    async (projectName: string) => {
      if (!projectName) return false;

      setValidating(true);
      setAccessValidated(null);

      try {
        const baseUrl = await discoveryApi.getBaseUrl('ado-proxy');
        const { token } = await identityApi.getCredentials();

        // Check if user has access to the project via backend proxy
        const projectRes = await fetchApi.fetch(
          `${baseUrl}/projects/${encodeURIComponent(projectName)}`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          },
        );

        if (!projectRes.ok) {
          setAccessValidated(false);
          setError(
            `Access denied to project '${projectName}'. Please check your permissions.`,
          );
          return false;
        }

        // Additional check: Try to get user's permissions for the project
        const permissionsRes = await fetchApi.fetch(
          `${baseUrl}/projects/${encodeURIComponent(projectName)}/teams`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          },
        );

        if (!permissionsRes.ok) {
          setAccessValidated(false);
          setError(`Insufficient permissions for project '${projectName}'.`);
          return false;
        }

        setAccessValidated(true);
        setError(undefined);
        return true;
      } catch (e: unknown) {
        setAccessValidated(false);
        const message = e instanceof Error ? e.message : String(e);
        setError(
          `Failed to validate access to project '${projectName}': ${message}`,
        );
        return false;
      } finally {
        setValidating(false);
      }
    },
    [fetchApi, discoveryApi, identityApi],
  );

  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      setError(undefined);
      try {
        const baseUrl = await discoveryApi.getBaseUrl('ado-proxy');
        const { token } = await identityApi.getCredentials();

        // Fetch all projects via backend proxy (handles pagination internally)
        const res = await fetchApi.fetch(`${baseUrl}/projects`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to fetch ADO projects');
        }

        const data = await res.json();
        setProjects(data.projects || []);
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : String(e);
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [fetchApi, discoveryApi, identityApi]);

  // Validate access for existing value when component mounts
  useEffect(() => {
    if (value && projects.length > 0) {
      validateProjectAccess(value);
    }
  }, [value, projects, validateProjectAccess]);

  return (
    <>
      <Autocomplete
        options={projects}
        getOptionLabel={option => option.name}
        value={projects.find(p => p.name === value) || null}
        onChange={async (_, newValue) => {
          const selectedProject = newValue?.name || '';

          if (selectedProject) {
            // Validate access to the selected project
            const hasAccess = await validateProjectAccess(selectedProject);
            if (hasAccess) {
              onChange(selectedProject);
            } else {
              // Don't update the value if access validation fails
              return;
            }
          } else {
            // Clear selection
            onChange('');
            setAccessValidated(null);
            setError(undefined);
          }
        }}
        disabled={disabled || loading || validating}
        loading={loading || validating}
        renderInput={params => (
          <TextField
            {...params}
            label="ADO Project"
            variant="outlined"
            fullWidth
            error={!!error}
            helperText={
              error ||
              (validating ? 'Validating project access...' : '') ||
              (accessValidated === true ? 'Project access validated ✓' : '') ||
              (accessValidated === false
                ? 'Project access validation failed ✗'
                : '')
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading || validating ? (
                    <CircularProgress size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </>
  );
};
